import { Car, TrendingUp, Award, AlertTriangle } from "lucide-react";

var STATUS_LABELS = { ativo: "Ativo", cancelado: "Cancelado", contemplado: "Contemplado", em_atraso: "Em Atraso" };
var STATUS_COLORS = {
  ativo: "text-emerald-600", cancelado: "text-gray-400",
  contemplado: "text-blue-600", em_atraso: "text-amber-600",
};
var STATUS_BG = {
  ativo: "bg-emerald-500", cancelado: "bg-gray-300",
  contemplado: "bg-blue-500", em_atraso: "bg-amber-500",
};
var STATUS_RING = {
  ativo: "ring-emerald-200", cancelado: "ring-gray-200",
  contemplado: "ring-blue-200", em_atraso: "ring-amber-200",
};

function formatCurrency(v) {
  if (v == null || v === 0) return "R$ 0";
  return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function ProgressRing({ paid, total, status }) {
  var pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  var r = 38;
  var circumference = 2 * Math.PI * r;
  var offset = circumference - (pct / 100) * circumference;
  var color = STATUS_BG[status] || "bg-gray-400";
  var strokeColor = status === "ativo" ? "#10B981" : status === "contemplado" ? "#3B82F6" : status === "em_atraso" ? "#F59E0B" : "#9CA3AF";

  return (
    <div className="relative w-24 h-24 flex-shrink-0">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 88 88">
        <circle cx="44" cy="44" r={r} fill="none" stroke="#F3F4F6" strokeWidth="6" />
        <circle cx="44" cy="44" r={r} fill="none" stroke={strokeColor} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-[#0A2F3D]">{pct}%</span>
        <span className="text-[10px] text-gray-400">{paid}/{total}</span>
      </div>
    </div>
  );
}

function ConsortiumCard({ product }) {
  var paid = product.installments_paid || 0;
  var total = product.installments_total || 0;
  var remaining = Math.max(0, total - paid);
  var status = product.status || "ativo";

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-testid={"consortium-dash-" + product.id}>
      <div className="flex items-start gap-4">
        <ProgressRing paid={paid} total={total} status={status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {product.grupo && <span className="px-2 py-0.5 text-xs font-bold rounded-md bg-[#F26A4B]/10 text-[#F26A4B]">G: {product.grupo}</span>}
            {product.cota && <span className="px-2 py-0.5 text-xs font-bold rounded-md bg-[#3B82F6]/10 text-[#3B82F6]">C: {product.cota}</span>}
            <span className={"text-xs font-semibold " + (STATUS_COLORS[status] || "text-gray-500")}>{STATUS_LABELS[status] || status}</span>
          </div>
          <p className="text-sm font-semibold text-[#0A2F3D] truncate">{product.administrator || "Consorcio"}</p>
          <p className="text-xs text-gray-400 mt-0.5">Carta: {formatCurrency(product.value)}</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Parcela</p>
              <p className="text-sm font-bold text-[#0A2F3D]">{formatCurrency(product.installment_value)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Restam</p>
              <p className="text-sm font-bold text-[#0A2F3D]">{remaining} parcela{remaining !== 1 ? "s" : ""}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConsortiumDashboard({ products }) {
  var consortiums = products.filter(function(p) { return p.product_type === "consorcio"; });
  if (consortiums.length === 0) return null;

  var totalValue = 0;
  var totalPaid = 0;
  var totalInstallments = 0;
  var contemplados = 0;
  var emAtraso = 0;

  for (var i = 0; i < consortiums.length; i++) {
    var c = consortiums[i];
    totalValue += (c.value || 0);
    totalPaid += (c.installments_paid || 0);
    totalInstallments += (c.installments_total || 0);
    if (c.status === "contemplado") contemplados++;
    if (c.status === "em_atraso") emAtraso++;
  }

  var overallPct = totalInstallments > 0 ? Math.round((totalPaid / totalInstallments) * 100) : 0;

  return (
    <div id="section-consortiums" className="mb-6 lg:mb-8 scroll-mt-24" data-testid="consortium-dashboard">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2.5 bg-[#F26A4B]/10 rounded-xl">
          <Car className="w-5 h-5 text-[#F26A4B]" />
        </div>
        <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D]">Meus Consorcios</h3>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-xl p-3.5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-1.5">
            <TrendingUp className="w-4 h-4 text-[#3B82F6]" />
            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Progresso</span>
          </div>
          <p className="text-xl font-bold text-[#0A2F3D]" data-testid="consortium-overall-pct">{overallPct}%</p>
          <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#3B82F6] rounded-full transition-all duration-1000" style={{ width: overallPct + "%" }} />
          </div>
        </div>
        <div className="bg-white rounded-xl p-3.5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-1.5">
            <Car className="w-4 h-4 text-[#F26A4B]" />
            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Total Cartas</span>
          </div>
          <p className="text-xl font-bold text-[#0A2F3D]" data-testid="consortium-total-value">{formatCurrency(totalValue)}</p>
        </div>
        <div className="bg-white rounded-xl p-3.5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-1.5">
            <Award className="w-4 h-4 text-blue-500" />
            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Contemplados</span>
          </div>
          <p className="text-xl font-bold text-blue-600" data-testid="consortium-contemplados">{contemplados}</p>
        </div>
        <div className="bg-white rounded-xl p-3.5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Em Atraso</span>
          </div>
          <p className={"text-xl font-bold " + (emAtraso > 0 ? "text-amber-600" : "text-[#0A2F3D]")} data-testid="consortium-em-atraso">{emAtraso}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {consortiums.map(function(c) { return <ConsortiumCard key={c.id} product={c} />; })}
      </div>
    </div>
  );
}
